import { supabase } from '../auth/supabase';
import { CONSENT_VERSION } from '../lib/config';
import type { DbClient } from '../db/client';
import { withTimeout } from './entitlement';

const SETTING_ID = 'consent';

interface ConsentSetting {
  version: string;
  accepted_at: string;
}

/**
 * True when this account has accepted the current disclaimer and health-data consent. Checks the
 * local settings row first; on a new device, falls back to the server record and caches it.
 */
export async function hasCurrentConsent(db: DbClient): Promise<boolean> {
  const [row] = await db.query<{ value: string | null }>('SELECT value FROM settings WHERE id = ? AND deleted_at IS NULL', [SETTING_ID]);
  if (row?.value && (JSON.parse(row.value) as ConsentSetting).version === CONSENT_VERSION) return true;

  if (!navigator.onLine) return false;
  const { data, error } = await withTimeout(supabase.from('consents').select('kind, accepted_at').eq('version', CONSENT_VERSION), 6000).catch(() => ({
    data: null,
    error: true
  }));
  if (error || !data) return false;
  const kinds = new Set(data.map((consent) => consent.kind));
  if (!kinds.has('disclaimer') || !kinds.has('health_data_processing')) return false;
  await saveLocal(db, data[0].accepted_at);
  return true;
}

/** Records consent on the server (required) and in the synced settings row. */
export async function recordConsent(db: DbClient) {
  const userAgent = navigator.userAgent.slice(0, 300);
  const offline = 'Can’t reach RenalFlow. Check your internet connection and try again.';
  if (!navigator.onLine) throw new Error(offline);
  const { error } = await withTimeout(
    supabase.from('consents').insert([
      { kind: 'disclaimer', version: CONSENT_VERSION, user_agent: userAgent },
      { kind: 'health_data_processing', version: CONSENT_VERSION, user_agent: userAgent }
    ]),
    10_000
  ).catch(() => ({ error: { message: offline } }));
  if (error) throw new Error(/fetch|network/i.test(error.message) ? offline : error.message);
  await saveLocal(db, new Date().toISOString());
}

async function saveLocal(db: DbClient, acceptedAt: string) {
  const value: ConsentSetting = { version: CONSENT_VERSION, accepted_at: acceptedAt };
  await db.upsert('settings', SETTING_ID, { value: JSON.stringify(value) });
}
